import { useState } from "react";

import { ApiFehler } from "../api/client";
import { useKundenLinkErzeugen, useKundenLinks, useKundenLinkSperren } from "../api/hooks";
import type { FrageTyp, KundenLink, VollmachtModell } from "../api/typen";
import { useToast } from "../kontext/ToastKontext";
import { datum, zeitpunkt } from "../lib/format";
import { kopiereText, linkFuerDieseAdresse } from "../lib/zwischenablage";
import { Hinweis, Karte, Knopf, Modal } from "./Basis";

const STATUS: Record<KundenLink["status"], { text: string; klasse: string }> = {
  offen: { text: "wartet auf Kunde", klasse: "bg-sky-50 text-sky-800 ring-sky-200" },
  abgeschlossen: { text: "beantwortet", klasse: "bg-ampel-gruenHell text-ampel-gruen ring-ampel-gruen/30" },
  abgelaufen: { text: "abgelaufen", klasse: "bg-neutral-100 text-neutral-600 ring-neutral-300" },
  gesperrt: { text: "gesperrt", klasse: "bg-neutral-100 text-neutral-600 ring-neutral-300" },
};

const TYP_TEXT: Record<FrageTyp, string> = { ja_nein: "Ja/Nein", text: "Text", datei: "Foto/Datei" };

function LinkModal({ link, schliessen }: { link: KundenLink; schliessen: () => void }) {
  const toast = useToast();
  const url = linkFuerDieseAdresse(link.link ?? "");
  async function kopieren() {
    if (await kopiereText(url)) toast.erfolg("Link kopiert", "Jetzt per WhatsApp, SMS oder E-Mail an den Kunden senden.");
    else toast.fehler(new Error("Kopieren nicht möglich – bitte den Link markieren und selbst kopieren."));
  }
  return (
    <Modal offen titel={link.art === "rueckfrage" ? "Rückfrage an den Kunden" : "Link für den Kunden"} beiSchliessen={schliessen} fuss={<Knopf variante="primaer" onClick={schliessen}>Fertig</Knopf>}>
      <div className="space-y-3 text-sm">
        <div className="flex gap-2">
          <input readOnly className="w-full rounded-lg border border-neutral-300 bg-neutral-50 px-3 py-2 font-mono text-xs" value={url} onFocus={(e) => e.target.select()} />
          <Knopf onClick={() => void kopieren()} data-autofokus>Kopieren</Knopf>
        </div>
        <p className="text-xs text-neutral-500">Gültig bis {datum(link.gueltig_bis)}. Nach dem Absenden ist der Link gesperrt. Der Kunde sieht vorab nur seinen Namen und den Ort.</p>
      </div>
    </Modal>
  );
}

function ErfassungDialog({ anlageId, schliessen, erzeugt }: { anlageId: number; schliessen: () => void; erzeugt: (l: KundenLink) => void }) {
  const erzeugen = useKundenLinkErzeugen(anlageId);
  const [modell, setModell] = useState<VollmachtModell | null>("betrieb");
  const [fehler, setFehler] = useState<string | null>(null);
  function absenden() {
    setFehler(null);
    erzeugen.mutate(
      { art: "erfassung", vollmacht_modell: modell, fragen: [] },
      { onSuccess: erzeugt, onError: (e) => setFehler(e instanceof ApiFehler ? e.message : "Fehlgeschlagen") },
    );
  }
  // Die Variante ergibt sich aus der Anlage: Kunde eines Betriebs → Vollmacht an den Betrieb, sonst an uns.
  const optionen: [VollmachtModell | null, string][] = [
    ["betrieb", "mit Vollmacht (an den Betrieb, bei eigenen Kunden an uns)"],
    [null, "ohne Vollmacht (liegt schon vor)"],
  ];
  return (
    <Modal
      offen
      titel="Angaben und Vollmacht anfragen"
      beiSchliessen={schliessen}
      fuss={<><Knopf onClick={schliessen}>Abbrechen</Knopf><Knopf variante="primaer" laedt={erzeugen.isPending} onClick={absenden}>Link erzeugen</Knopf></>}
    >
      <div className="space-y-3 text-sm">
        <p>Der Kunde gibt persönliche Daten, Anlagenstandort, Zählernummer (oder Foto) und Bankverbindung an.</p>
        {optionen.map(([wert, text]) => (
          <label key={String(wert)} className="flex items-center gap-2">
            <input type="radio" name="modell" checked={modell === wert} onChange={() => setModell(wert)} className="accent-neutral-900" />
            {text}
          </label>
        ))}
        <p className="text-xs text-neutral-500">Ein offener Link dieser Anlage wird dabei ungültig.</p>
        {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
      </div>
    </Modal>
  );
}

function RueckfrageDialog({ anlageId, schliessen, erzeugt }: { anlageId: number; schliessen: () => void; erzeugt: (l: KundenLink) => void }) {
  const erzeugen = useKundenLinkErzeugen(anlageId);
  const [fragen, setFragen] = useState<{ text: string; typ: FrageTyp }[]>([{ text: "", typ: "ja_nein" }]);
  const [fehler, setFehler] = useState<string | null>(null);
  const gueltig = fragen.every((f) => f.text.trim().length >= 3);
  function aendern(i: number, teil: Partial<{ text: string; typ: FrageTyp }>) {
    setFragen(fragen.map((f, j) => (j === i ? { ...f, ...teil } : f)));
  }
  function absenden() {
    setFehler(null);
    erzeugen.mutate(
      { art: "rueckfrage", vollmacht_modell: null, fragen: fragen.map((f) => ({ ...f, text: f.text.trim() })) },
      { onSuccess: erzeugt, onError: (e) => setFehler(e instanceof ApiFehler ? e.message : "Fehlgeschlagen") },
    );
  }
  return (
    <Modal
      offen
      breit
      titel="Rückfrage stellen"
      beiSchliessen={schliessen}
      fuss={<><Knopf onClick={schliessen}>Abbrechen</Knopf><Knopf variante="primaer" disabled={!gueltig} laedt={erzeugen.isPending} onClick={absenden}>Link erzeugen</Knopf></>}
    >
      <div className="space-y-3 text-sm">
        <p className="text-neutral-600">Zum Beispiel eine Sonderfrage des Netzbetreibers: „Sind Sie selbstständig tätig?“. Der Kunde antwortet mit einem Tipp.</p>
        {fragen.map((f, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2">
            <input
              data-autofokus={i === 0 ? true : undefined}
              className="min-w-0 flex-1 rounded-lg border border-neutral-300 px-3 py-2"
              placeholder={`Frage ${i + 1}`}
              maxLength={300}
              value={f.text}
              onChange={(e) => aendern(i, { text: e.target.value })}
            />
            <select className="rounded-lg border border-neutral-300 px-2 py-2" value={f.typ} onChange={(e) => aendern(i, { typ: e.target.value as FrageTyp })}>
              {(Object.keys(TYP_TEXT) as FrageTyp[]).map((t) => <option key={t} value={t}>{TYP_TEXT[t]}</option>)}
            </select>
            {fragen.length > 1 && <Knopf variante="geist" onClick={() => setFragen(fragen.filter((_, j) => j !== i))} aria-label="Frage entfernen">✕</Knopf>}
          </div>
        ))}
        {fragen.length < 10 && <Knopf variante="geist" onClick={() => setFragen([...fragen, { text: "", typ: "text" }])}>+ Weitere Frage</Knopf>}
        {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
      </div>
    </Modal>
  );
}

/** Backoffice: Kunden-Links einer Anlage – Angaben/Vollmacht anfragen, Rückfragen stellen, Antworten sehen. */
export function KundenLinks({ anlageId, beiAnsehen }: { anlageId: number; beiAnsehen: (dokumentId: number) => void }) {
  const links = useKundenLinks(anlageId);
  const sperren = useKundenLinkSperren(anlageId);
  const [dialog, setDialog] = useState<"erfassung" | "rueckfrage" | null>(null);
  const [anzeigen, setAnzeigen] = useState<KundenLink | null>(null);
  const liste = links.data ?? [];

  function erzeugt(link: KundenLink) {
    setDialog(null);
    setAnzeigen(link);
  }

  return (
    <Karte className="p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-semibold">Kunden-Link &amp; Rückfragen</h2>
          <p className="text-xs text-neutral-500">Der Kunde gibt seine Angaben selbst ein, unterschreibt die Vollmacht oder beantwortet Fragen.</p>
        </div>
        <div className="flex gap-2">
          <Knopf onClick={() => setDialog("erfassung")}>Angaben + Vollmacht anfragen</Knopf>
          <Knopf onClick={() => setDialog("rueckfrage")}>Rückfrage stellen</Knopf>
        </div>
      </div>

      {liste.length > 0 && (
        <ul className="mt-4 divide-y divide-neutral-100">
          {liste.map((l) => (
            <li key={l.id} className="py-3 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold">{l.art === "rueckfrage" ? "Rückfrage" : "Angaben" + (l.vollmacht_modell ? " + Vollmacht" : "")}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ring-inset ${STATUS[l.status].klasse}`}>{STATUS[l.status].text}</span>
                <span className="text-xs text-neutral-500">
                  {l.abgeschlossen_am ? `beantwortet ${zeitpunkt(l.abgeschlossen_am)}` : `erzeugt ${zeitpunkt(l.created_at)} · gültig bis ${datum(l.gueltig_bis)}`}
                </span>
                <span className="ml-auto flex gap-1">
                  {l.status === "offen" && (
                    <>
                      <Knopf variante="geist" className="text-xs" onClick={() => setAnzeigen(l)}>Link</Knopf>
                      <Knopf variante="geist" className="text-xs" onClick={() => window.confirm("Link sperren? Der Kunde kann ihn dann nicht mehr öffnen.") && sperren.mutate(l.id)}>Sperren</Knopf>
                    </>
                  )}
                  {l.dokument_id && <Knopf variante="geist" className="text-xs" onClick={() => beiAnsehen(l.dokument_id as number)}>PDF ansehen</Knopf>}
                </span>
              </div>
              {l.art === "rueckfrage" && l.antworten && (
                <dl className="mt-2 space-y-1 rounded-lg bg-neutral-50 p-3">
                  {l.antworten.map((a) => (
                    <div key={a.frage_id} className="grid gap-1 sm:grid-cols-[1fr_auto]">
                      <dt className="text-neutral-600">{a.frage}</dt>
                      <dd className="font-semibold">
                        {a.typ === "datei"
                          ? a.dokument_ids.length
                            ? a.dokument_ids.map((id, i) => (
                                <button key={id} type="button" className="ml-2 underline" onClick={() => beiAnsehen(id)}>Datei {i + 1}</button>
                              ))
                            : "–"
                          : typeof a.wert === "boolean"
                            ? a.wert ? "Ja" : "Nein"
                            : (a.wert ?? "–")}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
            </li>
          ))}
        </ul>
      )}

      {dialog === "erfassung" && <ErfassungDialog anlageId={anlageId} schliessen={() => setDialog(null)} erzeugt={erzeugt} />}
      {dialog === "rueckfrage" && <RueckfrageDialog anlageId={anlageId} schliessen={() => setDialog(null)} erzeugt={erzeugt} />}
      {anzeigen && <LinkModal link={anzeigen} schliessen={() => setAnzeigen(null)} />}
    </Karte>
  );
}
